/**
 * A small least-recently-used cache for string results. Bounded (both in
 * entries, and in the length of keys it'll hold) since this code also runs
 * in long-lived API processes, and some inputs are arbitrary user text.
 */
export class BoundedCache {
  private readonly entries = new Map<string, string>();
  private readonly maxEntries: number;
  private readonly maxKeyLength: number;

  constructor(maxEntries = 1000, maxKeyLength = 300) {
    this.maxEntries = maxEntries;
    this.maxKeyLength = maxKeyLength;
  }

  /**
   * Returns the cached value for `key`, or computes, caches, and returns it
   */
  get(key: string, compute: () => string): string {
    if (key.length > this.maxKeyLength) return compute();

    const hit = this.entries.get(key);
    if (hit !== undefined) {
      // re-insert, so Map's insertion order doubles as recency order
      this.entries.delete(key);
      this.entries.set(key, hit);
      return hit;
    }

    const value = compute();
    if (this.entries.size >= this.maxEntries) {
      this.entries.delete(this.entries.keys().next().value!);
    }
    this.entries.set(key, value);
    return value;
  }
}
